import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'

type Usage = Awaited<ReturnType<typeof api.eodhdUsage>>

const SOURCE: Record<string, string> = {
  'app:quotes': 'Ticker tape & lists',
  'app:extended': 'Extended-hours quotes',
  'app:ohlc': 'Charts',
  'app:technicals': 'Technicals',
  'app:profile': 'Company profiles',
  'app:news': 'News',
  'app:search': 'Symbol search',
  'app:workers': 'Workers & journal',
  ai: 'AI (chat)',
  'ai:report': 'AI (scheduled reports)',
  'ai:wake-task': 'AI (scheduled wakeups)',
  'ai:wake-watch': 'AI (watcher wakeups)',
  'ai:wake-scan': 'AI (scan analyses)',
  'ai:wake-worker': 'AI (worker callbacks)',
  watch: 'Watchers',
  scan: 'Scans',
  snapshot: 'Portfolio snapshots',
  other: 'Other',
}
const fmt = (n: number) => n.toLocaleString('en-US')

/**
 * Market-data calls today — EODHD's own counter against the plan's daily allowance (it resets at
 * midnight GMT) and what this server counted, by source, so a leak shows where it comes from.
 */
export function MarketDataUsage() {
  const [u, setU] = useState<Usage | null>(null)
  const [err, setErr] = useState<string | null>(null)
  useEffect(() => {
    const load = () =>
      api
        .eodhdUsage()
        .then((x) => {
          setU(x)
          setErr(null)
        })
        .catch((e) => setErr(e instanceof Error ? e.message : 'unavailable'))
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [])
  const a = u?.account
  const cap = a ? a.limit + a.extra : 0
  const pct = a && cap ? Math.min(1, a.used / cap) : 0
  const rows = u ? Object.entries(u.today.bySource).sort((x, y) => y[1] - x[1]) : []
  return (
    <section id="settings-marketdata" className="card p-4">
      <h2 className="text-[14px] font-semibold text-fg">Market data (EODHD)</h2>
      <p className="mt-0.5 text-[12.5px] text-fg-subtle">
        API calls today against the plan’s daily allowance — it resets at midnight GMT (03:00 in Turkey). A live quote costs one call per symbol;
        intraday bars, news and screeners 5; fundamentals 10.
      </p>
      {err && !u ? (
        <div className="mt-3 text-[12px] text-fg-faint">{err}</div>
      ) : !u ? (
        <div className="mt-3 h-10 animate-pulse rounded-lg bg-[var(--color-inset)]" />
      ) : (
        <>
          {a && (
            <div className="mt-3">
              <div className="flex items-baseline justify-between text-[12.5px]">
                <span className="text-fg-muted">EODHD’s counter</span>
                <span className={cn('num', pct > 0.9 ? 'text-down' : 'text-fg')}>
                  {fmt(a.used)} <span className="text-fg-faint">/ {fmt(cap)}</span>
                </span>
              </div>
              <div className="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-[var(--color-inset)]">
                <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.max(1, pct * 100)}%`, background: pct > 0.9 ? 'var(--color-down)' : 'var(--color-fg-muted)' }} />
              </div>
            </div>
          )}
          <div className="mt-3 text-[11px] uppercase tracking-wide text-fg-faint">Counted by this server since {u.today.day} 00:00 GMT · {fmt(u.today.total)}</div>
          <div className="mt-1.5 space-y-1">
            {rows.length === 0 && <div className="text-[12px] text-fg-faint">Nothing yet.</div>}
            {rows.map(([k, n]) => (
              <div key={k} className="grid grid-cols-[1fr_auto] items-center gap-3 text-[12px]">
                <span className="text-fg-subtle">{SOURCE[k] ?? k}</span>
                <span className="num text-fg-muted">{fmt(n)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
